// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { usePetitionQRCode } from '#src/app/shared/hooks/qrcode';

const QR_CODE_TYPE = 'WEB_PSF_PROMOTE_OR_SHARE';

/**
 * The slide-up's QR code. `idle` until the first open in a treatment arm (and forever in production),
 * `loading` while the query and its retries run, `failed` once they are exhausted or when the image data
 * cannot be decoded. `ready` carries the image, its share id, and the PNG `File` Save shares, built as
 * soon as the data arrived so the tap has nothing left to await.
 */
export type SlideUpQrCode = Readonly<
	| { status: 'idle' | 'loading' | 'failed'; imageBase64Uri: null; shareId: null; file: null }
	| { status: 'ready'; imageBase64Uri: string; shareId: string | null; file: File }
>;

type UseSlideUpQrCodeResult = {
	qrCode: SlideUpQrCode;
	/**
	 * Starts the QR fetch on the slide-up's first open, and retries it on a later open after the request
	 * failed. A no-op outside treatment arms, while the code is ready or loading, and after a decode failure,
	 * which stays failed for the page view because the data never goes stale.
	 */
	startFetch: () => void;
};

const NOT_READY = { imageBase64Uri: null, shareId: null, file: null } as const;

type FileResult = { file: File } | { file: null; error: unknown };

const PNG_DATA_URI_PREFIX = 'data:image/png;base64,';

function dataUriToPngFile(dataUri: string, filename: string): File {
	if (!dataUri.startsWith(PNG_DATA_URI_PREFIX) || dataUri.length === PNG_DATA_URI_PREFIX.length) {
		throw new Error('The QR code image is not a base64 PNG data URI');
	}
	const binary = atob(dataUri.slice(PNG_DATA_URI_PREFIX.length));
	const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
	return new File([bytes], filename, { type: 'image/png' });
}

/**
 * Owns the share slide-up's screen QR code for the page view. The query only runs once `startFetch` is
 * called in a treatment arm, and its data never goes stale, so closing and reopening the slide-up shows
 * the same code and share id without a second request.
 */
export function useSlideUpQrCode(isTreatmentArm: boolean): UseSlideUpQrCodeResult {
	const {
		data: { petition },
	} = usePsfPromoteOrSharePageData();
	const { translate } = useI18n();
	const { errorReporter } = useUtilityContext();

	const [shouldFetch, setShouldFetch] = useState(false);
	const isFetchStarted = shouldFetch && isTreatmentArm;

	// No size and no pixel-ratio adjustment: the type's fixed server size serves both the screen and the
	// saved file.
	const { data, isError, isFetching, refetch } = usePetitionQRCode({
		slugOrId: petition.id,
		type: QR_CODE_TYPE,
		enabled: isFetchStarted,
		staleTime: 'static',
	});
	const canRetry = isFetchStarted && isError && !isFetching;

	const startFetch = useCallback(() => {
		if (!isTreatmentArm) return;
		setShouldFetch(true);
		// A retry runs its own retries. `usePetitionQRCode` tracks the failure once per mount, so a second
		// failure is not tracked again.
		if (canRetry) void refetch();
	}, [isTreatmentArm, canRetry, refetch]);

	const filename = `${translate('corgi.petition.details.qr_code.filename')}.png`;
	// Another slide-up on the page can fill the shared cache, so wait for this one's own open.
	const fileResult = useMemo((): FileResult | null => {
		if (!isFetchStarted || !data) return null;
		try {
			return { file: dataUriToPngFile(data.imageBase64Uri, filename) };
		} catch (error) {
			return { file: null, error };
		}
	}, [isFetchStarted, data, filename]);

	useEffect(() => {
		if (fileResult && !fileResult.file) {
			void errorReporter.report(
				{
					error: new Error('Could not build the slide-up QR code file', { cause: fileResult.error }),
					params: { petitionId: petition.id },
					context: { feature: 'psf_promote_or_share_qr_code_experiment', requestId: data?.requestId },
				},
				{ severity: 'warning' },
			);
		}
	}, [fileResult, data?.requestId, errorReporter, petition.id]);

	const qrCode = useMemo((): SlideUpQrCode => {
		if (data && fileResult?.file) {
			return {
				status: 'ready',
				imageBase64Uri: data.imageBase64Uri,
				shareId: data.shareId,
				file: fileResult.file,
			};
		}
		// A retry with no cached data resets the query to pending, so `isError` is false while it runs.
		if (isFetching) return { status: 'loading', ...NOT_READY };
		if (isError || fileResult) return { status: 'failed', ...NOT_READY };
		return { status: isFetchStarted ? 'loading' : 'idle', ...NOT_READY };
	}, [data, fileResult, isError, isFetching, isFetchStarted]);

	return { qrCode, startFetch };
}
